import {
    GET_MISSION_OVERTIME_STATUS,
    GET_MISSION_VACATION_ACCUMULATION_MINUTES_FOR_PAYROLL_BREAKDOWN,
    GET_MISSION_VACATION_STATUS,
    GET_PAYROLL_BREAKDOWN,
    GET_PAYROLL_BREAKDOWN_SUMMARY,
    SUBMIT_PAYROLL_BREAKDOWN,
    SAVE_PAYROLL_BREAKDOWN,
} from "../../graphql/timesheet";
import {
    buildAuxiliarySalary,
    buildContract,
    buildMission,
    buildMissionDeduction,
    buildMissionFee,
    buildMissionInfo,
    buildMissionOvertimeStatus,
    buildMissionVacationStatus,
    buildPayrollBreakdown,
    buildPayrollBreakdownSummary,
    buildTimesheet,
    buildUser,
    buildWorkerInfo,
    EARNING_TYPE_SAMPLES,
} from "../factories";
import { MockedAuthManager } from "../../contexts/AuthManager";
import { MockedProvider } from "@apollo/client/testing/react";
import React, { useMemo } from "react";
import {
    EarningType,
    PayrollBreakdownInput,
    PayrollBreakdownStatus,
    PayrollSourceType,
    ProfileType,
    TimeSheet,
    TimeSheetState,
} from "../../__generated__/graphql";
import type { Meta, StoryObj } from "@storybook/react";
import PayrollBreakdownEditorScreen from "../../screens/client/PayrollBreakdownEditorScreen";
import { navigatorLogger } from "../navigator-mock";
import { GET_MISSION } from "../../graphql/core";
import { MockedProfileSwitcherManager } from "../../contexts/ProfileSwitcherManager";

/**
 * The way the MockProvider works is that we cannot modify the result() nor newData() functions
 * after its creation so we need to create a function that pass dynamic parameters using
 * IDs for the payroll breakdowns and mission.
 * The ID is created using the buildPayrollBreakdownInput
 */
const parsePayrollBreakdownId = (payrollBreakdownId: string) => {
    const parts = payrollBreakdownId.split("-");
    const emptyPeriod = parts[5] === "true";
    return {
        status: parts[0] as PayrollBreakdownStatus,
        earningType: parts[1] as EarningType,
        timesheetsStatus: parts[2],
        missionId: parts[3],
        payrollSource: parts[4] as PayrollSourceType,
        withAuxiliarySalaries: parts[5] === "aux",
        startDate: emptyPeriod ? new Date("2024-01-20") : undefined,
        endDate: emptyPeriod ? new Date("2024-01-17") : undefined,
    };
};

const buildPayrollBreakdownId = ({
    status,
    earningType,
    hasWorkDays,
    timesheetsStatus,
    payrollSource,
    emptyPeriod,
    withAuxiliarySalaries,
}: {
    status: PayrollBreakdownStatus;
    earningType: EarningType;
    hasWorkDays: boolean;
    timesheetsStatus: string;
    payrollSource: PayrollSourceType;
    emptyPeriod?: boolean;
    withAuxiliarySalaries?: boolean;
}) => {
    const missionId = buildMissionId({ hasWorkDays, payrollSource, earningType });
    if (emptyPeriod) {
        return `${status}-${earningType}-${timesheetsStatus}-${missionId}-${payrollSource}-${emptyPeriod}`;
    }
    if (withAuxiliarySalaries) {
        return `${status}-${earningType}-${timesheetsStatus}-${missionId}-${payrollSource}-aux`;
    }
    return `${status}-${earningType}-${timesheetsStatus}-${missionId}-${payrollSource}`;
};

const buildMissionId = (input: {
    hasWorkDays: boolean;
    payrollSource: PayrollSourceType;
    earningType: EarningType;
}) => {
    return `${input.hasWorkDays ? "workDays" : "noWorkDays"}/${input.payrollSource}/${input.earningType}`;
};

const parseMissionId = (missionId: string) => {
    const parts = missionId.split("/");
    return {
        hasWorkDays: parts[0] === "workDays",
        payrollSource: parts[1],
        earningType: parts[2] as EarningType,
    };
};

const MOCKS = [
    {
        request: {
            query: GET_PAYROLL_BREAKDOWN,
            variables: () => true,
        },
        maxUsageCount: Number.POSITIVE_INFINITY,
        result: ({ payrollBreakdownId }: { payrollBreakdownId: string }) => {
            const inputData = parsePayrollBreakdownId(payrollBreakdownId);
            let timeSheets: TimeSheet[] = [];
            if (inputData.payrollSource === PayrollSourceType.Lift) {
                if (inputData.timesheetsStatus === "completed") {
                    timeSheets = [
                        buildTimesheet({
                            status: TimeSheetState.Submitted,
                        }),
                        buildTimesheet({
                            status: TimeSheetState.Submitted,
                        }),
                        buildTimesheet({
                            status: TimeSheetState.Submitted,
                        }),
                    ];
                } else if (inputData.timesheetsStatus === "open") {
                    timeSheets = [
                        buildTimesheet({
                            status: TimeSheetState.Open,
                        }),
                        buildTimesheet({
                            status: TimeSheetState.Submitted,
                        }),
                        buildTimesheet({
                            status: TimeSheetState.Accepted,
                        }),
                    ];
                } else if (inputData.timesheetsStatus === "rejected") {
                    timeSheets = [
                        buildTimesheet({
                            status: TimeSheetState.Open,
                        }),
                        buildTimesheet({
                            status: TimeSheetState.Rejected,
                        }),
                        buildTimesheet({
                            status: TimeSheetState.Accepted,
                        }),
                    ];
                }
            }
            return {
                data: {
                    getPayrollBreakdown: buildPayrollBreakdown(new Date(2024, 1, 1), {
                        ...(inputData.startDate && {
                            startDate: inputData.startDate.toISOString(),
                        }),
                        ...(inputData.endDate && {
                            endDate: inputData.endDate.toISOString(),
                        }),
                        status: inputData.status,
                        missionInfo: buildMissionInfo({
                            id: inputData.missionId,
                            earningType: inputData.earningType,
                            payrollSource: inputData.payrollSource,
                        }),
                        timesheets: timeSheets,
                        ...(inputData.withAuxiliarySalaries
                            ? { auxiliarySalaries: [buildAuxiliarySalary()] }
                            : {}),
                    }),
                },
            };
        },
    },
    {
        maxUsageCount: Number.POSITIVE_INFINITY,
        request: {
            query: GET_MISSION,
            variables: () => true,
        },
        result: ({ id }: { id: string }) => {
            const { hasWorkDays, payrollSource, earningType } = parseMissionId(id);
            return {
                data: {
                    getMission: buildMission({
                        currentContract: buildContract({
                            fees: hasWorkDays ? [buildMissionFee()] : [],
                            deductions: hasWorkDays ? [buildMissionDeduction()] : [],
                            earningType,
                            workingTimeClassicSpec: EARNING_TYPE_SAMPLES[earningType],
                        }),
                        payrollSource:
                            payrollSource === "lift"
                                ? PayrollSourceType.Lift
                                : PayrollSourceType.ManualClient,
                    }),
                },
            };
        },
    },
    {
        maxUsageCount: Number.POSITIVE_INFINITY,
        request: {
            query: GET_MISSION_VACATION_STATUS,
            variables: () => true,
        },
        result: () => {
            return {
                data: {
                    getMissionVacationStatus: buildMissionVacationStatus({
                        availableVacationMinutes: 180, // 3 hours
                        availablePendingVacationMinutes: 60, // 1 hour available not yet approved
                    }),
                },
            };
        },
    },
    {
        maxUsageCount: Number.POSITIVE_INFINITY,
        request: {
            query: GET_MISSION_VACATION_ACCUMULATION_MINUTES_FOR_PAYROLL_BREAKDOWN,
            variables: () => true,
        },
        result: () => {
            return {
                data: {
                    getMissionVacationAccumulationMinutesForPayrollBreakdown: 1058,
                },
            };
        },
    },
    {
        maxUsageCount: Number.POSITIVE_INFINITY,
        request: {
            query: GET_MISSION_OVERTIME_STATUS,
            variables: () => true,
        },
        result: () => {
            return {
                data: {
                    getMissionOvertimeStatus: buildMissionOvertimeStatus({
                        overtimePaidAtPercent: 125,
                    }),
                },
            };
        },
    },
    {
        maxUsageCount: Number.POSITIVE_INFINITY,
        request: {
            query: SUBMIT_PAYROLL_BREAKDOWN,
            variables: () => true,
        },
        result: ({
            payrollBreakdownId,
        }: {
            payrollBreakdownId: string;
            data: PayrollBreakdownInput;
        }) => {
            const { earningType, missionId } = parsePayrollBreakdownId(payrollBreakdownId);
            return {
                data: {
                    submitPayrollBreakdown: buildPayrollBreakdown(new Date(2024, 1, 1), {
                        id: payrollBreakdownId,
                        status: PayrollBreakdownStatus.Submitted,
                        missionInfo: buildMissionInfo({
                            id: missionId,
                            earningType,
                        }),
                    }),
                },
            };
        },
    },
    {
        maxUsageCount: Number.POSITIVE_INFINITY,
        request: {
            query: SAVE_PAYROLL_BREAKDOWN,
            variables: () => true,
        },
        result: ({
            payrollBreakdownId,
        }: {
            payrollBreakdownId: string;
            data: PayrollBreakdownInput;
        }) => {
            const { earningType, missionId } = parsePayrollBreakdownId(payrollBreakdownId);
            return {
                data: {
                    submitPayrollBreakdown: buildPayrollBreakdown(new Date(2024, 1, 1), {
                        id: payrollBreakdownId,
                        status: PayrollBreakdownStatus.Submitted,
                        missionInfo: buildMissionInfo({
                            id: missionId,
                            earningType,
                        }),
                    }),
                },
            };
        },
    },
    {
        maxUsageCount: Number.POSITIVE_INFINITY,
        request: {
            query: GET_PAYROLL_BREAKDOWN_SUMMARY,
            variables: () => true,
        },
        result: ({
            payrollBreakdownInput,
        }: {
            payrollBreakdownId: string;
            payrollBreakdownInput: PayrollBreakdownInput;
        }) => {
            const summary = buildPayrollBreakdownSummary();
            return {
                data: {
                    getPayrollBreakdownSummary: buildPayrollBreakdownSummary({
                        rows: [
                            ...summary.rows,
                            ...(payrollBreakdownInput?.bonuses || []).map((b) => ({
                                __typename: "SummaryRow",
                                category: "bonus",
                                productKey: "salary_bonus",
                                quantity: 1,
                                // fake invoice amount incl. charges, the real one is server-computed
                                unitPrice: Math.round(b.amount * 1.2),
                                description: b.description,
                                detailedDescription: null,
                                repetition: null,
                            })),
                            ...(payrollBreakdownInput?.auxiliarySalaries || []).map((a) => ({
                                __typename: "SummaryRow",
                                category: "auxiliary_salary",
                                productKey: "salary_hourly",
                                quantity: 1,
                                // fake all-inclusive invoice amount, the real one is server-computed
                                unitPrice: Math.round(a.grossAmount * 1.2),
                                description: `${a.description} – ${Math.floor(a.duration / 60)}h${String(a.duration % 60).padStart(2, "0")}`,
                                detailedDescription: null,
                                repetition: null,
                            })),
                        ],
                    }),
                },
            };
        },
    },
];

const StoryComponent = ({
    profileType,
    earningType,
    hasWorkDays,
    payrollSource,
    timesheetsStatus,
    payrollStatus,
    emptyPeriod,
    withAuxiliarySalaries,
}: {
    profileType: ProfileType;
    earningType: EarningType;
    hasWorkDays: boolean;
    payrollSource: PayrollSourceType;
    timesheetsStatus: string;
    payrollStatus: PayrollBreakdownStatus;
    emptyPeriod?: boolean;
    withAuxiliarySalaries?: boolean;
}) => {
    const user = useMemo(
        () =>
            buildUser({
                workerInfo: buildWorkerInfo(),
            }),
        [],
    );
    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={profileType}>
                <MockedProvider mocks={MOCKS}>
                    <PayrollBreakdownEditorScreen
                        /* @ts-expect-error Stack Component need a complex navigation handler */
                        route={{
                            params: {
                                payrollBreakdownId: buildPayrollBreakdownId({
                                    status: payrollStatus,
                                    earningType,
                                    timesheetsStatus,
                                    hasWorkDays,
                                    payrollSource,
                                    emptyPeriod,
                                    withAuxiliarySalaries,
                                }),
                            },
                        }}
                        /* @ts-expect-error Stack Component need a complex navigation handler */
                        navigation={navigatorLogger}
                    />
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
};

const meta = {
    title: "Screens/PayrollBreakdown Editor",
    component: StoryComponent,
    argTypes: {
        profileType: {
            options: Object.values(ProfileType),
            control: {
                type: "select",
            },
        },
        earningType: {
            options: Object.values(EarningType),
            control: {
                type: "select",
            },
        },
        hasWorkDays: {
            control: {
                type: "boolean",
            },
        },
        timesheetsStatus: {
            options: ["completed", "open", "rejected"],
            control: {
                type: "select",
            },
        },
        payrollSource: {
            options: Object.values(PayrollSourceType),
            control: {
                type: "select",
            },
        },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const LiftHourlyAllTS: Story = {
    args: {
        profileType: ProfileType.Client,
        earningType: EarningType.Hourly,
        hasWorkDays: true,
        payrollSource: PayrollSourceType.Lift,
        timesheetsStatus: "completed",
        payrollStatus: PayrollBreakdownStatus.Open,
    },
};

export const LiftMonthlyMissingTS: Story = {
    args: {
        profileType: ProfileType.Client,
        earningType: EarningType.Monthly,
        hasWorkDays: true,
        payrollSource: PayrollSourceType.Lift,
        timesheetsStatus: "open",
        payrollStatus: PayrollBreakdownStatus.Open,
    },
};
export const LiftMonthlyAllTS: Story = {
    args: {
        profileType: ProfileType.Client,
        earningType: EarningType.Monthly,
        hasWorkDays: true,
        payrollSource: PayrollSourceType.Lift,
        timesheetsStatus: "completed",
        payrollStatus: PayrollBreakdownStatus.Open,
    },
};

export const ManuallyHourly: Story = {
    args: {
        profileType: ProfileType.Client,
        earningType: EarningType.Hourly,
        hasWorkDays: true,
        payrollSource: PayrollSourceType.ManualClient,
        timesheetsStatus: "completed",
        payrollStatus: PayrollBreakdownStatus.Open,
    },
};

export const ManuallyHourlyWithAuxiliarySalaries: Story = {
    args: {
        profileType: ProfileType.Client,
        earningType: EarningType.Hourly,
        hasWorkDays: true,
        payrollSource: PayrollSourceType.ManualClient,
        timesheetsStatus: "completed",
        payrollStatus: PayrollBreakdownStatus.Open,
        withAuxiliarySalaries: true,
    },
};

export const ManuallyMonthly: Story = {
    args: {
        profileType: ProfileType.Client,
        earningType: EarningType.Monthly,
        hasWorkDays: false,
        payrollSource: PayrollSourceType.ManualClient,
        timesheetsStatus: "completed",
        payrollStatus: PayrollBreakdownStatus.Open,
    },
};

export const HourlyAlreadySubmitted: Story = {
    args: {
        profileType: ProfileType.Client,
        earningType: EarningType.Hourly,
        hasWorkDays: true,
        payrollSource: PayrollSourceType.Lift,
        timesheetsStatus: "completed",
        payrollStatus: PayrollBreakdownStatus.Submitted,
    },
};

export const MonthlyAlreadySubmitted: Story = {
    args: {
        profileType: ProfileType.Client,
        earningType: EarningType.Monthly,
        hasWorkDays: true,
        payrollSource: PayrollSourceType.Lift,
        timesheetsStatus: "completed",
        payrollStatus: PayrollBreakdownStatus.Submitted,
    },
};

export const ManualPackage: Story = {
    args: {
        profileType: ProfileType.Client,
        earningType: EarningType.Package,
        hasWorkDays: true,
        payrollSource: PayrollSourceType.ManualClient,
        timesheetsStatus: "completed",
        payrollStatus: PayrollBreakdownStatus.Open,
    },
};

export const ManualDaily: Story = {
    args: {
        profileType: ProfileType.Client,
        earningType: EarningType.Daily,
        hasWorkDays: true,
        payrollSource: PayrollSourceType.ManualClient,
        timesheetsStatus: "completed",
        payrollStatus: PayrollBreakdownStatus.Open,
    },
};

export const ManualEmptyPeriodMonthly: Story = {
    args: {
        profileType: ProfileType.Client,
        earningType: EarningType.Monthly,
        hasWorkDays: false,
        payrollSource: PayrollSourceType.ManualClient,
        timesheetsStatus: "completed",
        payrollStatus: PayrollBreakdownStatus.Open,
        emptyPeriod: true,
    },
};
